"use client";

import { useState, useCallback } from "react";
import { AlertType } from "@/components/shared/AlertModal";

interface AlertConfig {
  title?: string;
  message: string;
  type?: AlertType;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
}

export function useAlert() {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<AlertConfig>({
    message: "",
    type: "info",
  });

  const showAlert = useCallback((alertConfig: AlertConfig) => {
    setConfig(alertConfig);
    setIsOpen(true);
  }, []);

  const showConfirm = useCallback(
    (message: string, onConfirm: () => void, title?: string) => {
      setConfig({
        title: title || "Confirm",
        message,
        type: "confirm",
        confirmText: "Confirm",
        cancelText: "Cancel",
        onConfirm,
      });
      setIsOpen(true);
    },
    []
  );

  const showError = useCallback((message: string, title?: string) => {
    setConfig({
      title: title || "Error",
      message,
      type: "error",
    });
    setIsOpen(true);
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    setConfig({
      title: title || "Success",
      message,
      type: "success",
    });
    setIsOpen(true);
  }, []);

  const showWarning = useCallback((message: string, title?: string) => {
    setConfig({
      title: title || "Warning",
      message,
      type: "warning",
    });
    setIsOpen(true);
  }, []);

  const showInfo = useCallback((message: string, title?: string) => {
    setConfig({
      title: title,
      message,
      type: "info",
    });
    setIsOpen(true);
  }, []);

  const closeAlert = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    config,
    showAlert,
    showConfirm,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    closeAlert,
  };
}
